import React, { useState, useEffect } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import store, { loanActions } from '@apply/adminStore';
import { useSelector } from 'react-redux';
import { FileSelectDialog, InformModal } from '../../common/SharedComponents';
import { LoanApi, ListApi, FileApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { RefreshEvents } from '../../common/SharedEvents';
import { LoadPanel } from 'devextreme-react/load-panel';
import DateBox from 'devextreme-react/date-box';
import { IncompleteReasonType } from '../../loans/common/LoanConstants';
import { handleAdminError, handleAdminSuccess } from '../../common/SharedFunctions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil, faQuestionCircle } from '@fortawesome/free-solid-svg-icons';

const styles = {
    buttonMarginRight: { marginRight: '10px' },
    buttonContainer: {
        textAlign: 'right',
        borderTop: '2px solid silver',
        padding: '10px 10px',
    },
    form: { paddingLeft: '5px', paddingTop: '10px' },
    formRow: { marginBottom: '0' },
};

const LoseLoanModal = () => {
    const { isOpen, loanId } = useSelector((state) => state.loan.loseLoanModal);
    const { dispatch } = store;

    const [loan, set_loan] = useState(null);
    const [incompleteReasons, set_incompleteReasons] = useState([]);

    const [isManual, set_isManual] = useState(false);
    const [lostTypeId, set_lostTypeId] = useState(0);
    const [reason, set_reason] = useState('');
    const [withdrawalDate, set_withdrawalDate] = useState('');
    const [noticeDate, set_noticeDate] = useState('');
    const [noticeFile, set_noticeFile] = useState(null);
    const [sentByUserId, set_sentByUserId] = useState('');
    const [incompleteReasonTypeIds, set_incompleteReasonTypeIds] = useState([]);
    const [companyTaxReturnAdditionalInfo, set_companyTaxReturnAdditionalInfo] = useState('');
    const [personalTaxReturnAdditionalInfo, set_personalTaxReturnAdditionalInfo] = useState('');
    const [employees, set_employees] = useState([]);

    //File Upload
    const fileUploadOptions = {
        isOpen: false,
        maxFiles: 1,
        isReplace: false,
        typeName: '',
    };
    const [fileSelectOptions, set_fileSelectOptions] = useState(fileUploadOptions);

    const [loadingOpen, set_loadingOpen] = useState(false);
    const [loadingMessage, set_loadingMessage] = useState('Loading...');

    const [informModalTitle, set_informModalTitle] = useState('');
    const [informModalOpen, set_informModalOpen] = useState(false);
    const [informModalText, set_informModalText] = useState('');

    const loanApi = new LoanApi.LoanApi(applicationInsights);
    const listApi = new ListApi.ListApi(applicationInsights);
    const fileApi = new FileApi.FileApi(applicationInsights);

    const refreshEvents = new RefreshEvents();

    const lostOptions = [
        {
            id: 0,
            name: 'Counteroffer to Original Request Made by BFI and Not Accepted by Applicant',
        },
        { id: 1, name: 'Expressly Withdrawn by Applicant' },
        { id: 2, name: 'File Closed for Incompleteness' },
        { id: 3, name: 'Other' },
    ];

    useEffect(() => {
        const loadListsAsync = async () => {
            await _loadBfiEmployees();
        };
        loadListsAsync();
    }, []);

    useEffect(() => {
        async function isOpenChanged() {
            resetState();

            if (!isOpen || !loanId) {
                return;
            }

            if (loanId && loanId > 0) {
                await loadLoanInfo(loanId);
            }
        }

        isOpenChanged();
    }, [isOpen, loanId]);

    useEffect(() => {
        async function loanChanged() {
            if (!loan) {
                return;
            }

            await loadIncompleteReasons(loan);
            set_isManual(false);
            set_loadingOpen(false);
        }

        loanChanged();
    }, [loan]);

    const _handleClose = () => dispatch(loanActions.setLoseLoanModal({ loseLoanModal: { isOpen: false } }));

    const _loadBfiEmployees = async () => {
        const data = await listApi.getBfiEmployees();
        set_employees(data);
    };

    const loadLoanInfo = async (id) => {
        try {
            set_loadingMessage('Loading...');
            set_loadingOpen(true);

            const response = await loanApi.getLoanSummary(id);
            if (response.statusCode !== 200) {
                return;
            }
            const ln = response.result;
            set_loan(ln);
        } finally {
            set_loadingOpen(false);
        }
    };

    const resetState = () => {
        set_loan(null);
        set_isManual(false);
        set_noticeFile(null);
        set_sentByUserId('');
        set_reason('');
        set_incompleteReasonTypeIds([]);
        set_companyTaxReturnAdditionalInfo('');
        set_personalTaxReturnAdditionalInfo('');
        set_withdrawalDate('');
        set_noticeDate('');
        set_loadingOpen(false);
    };

    const loadIncompleteReasons = async (loan) => {
        set_loadingMessage('Loading...');
        set_loadingOpen(true);

        const response = await listApi.getIncompleteReasonsForLoan(loan.id);
        set_incompleteReasons(response.result);
    };

    const canSubmit = () => {
        let isOk = true;
        switch (lostTypeId) {
            case 1: //Withdrawn by Client
                isOk = withdrawalDate;
                break;
            case 2: //Incomplete file
                isOk = incompleteReasonTypeIds && incompleteReasonTypeIds.length > 0;
                if (isManual) {
                    isOk = isOk && noticeDate && sentByUserId && noticeFile;
                }
                break;
            case 3: //Other
                isOk = !!reason;
                break;
        }
        return isOk;
    };

    const onSubmit = async (e) => {
        e.preventDefault();

        try {
            const model = { lostTypeId };
            switch (lostTypeId) {
                case 0: //CounterOffer
                    break;
                case 1: //Withdrawn by Applicant
                    model.withdrawalDate = withdrawalDate.toLocaleDateString('en-us');
                    break;
                case 2: //Incomplete File
                    model.incompleteReasonTypeIds = incompleteReasonTypeIds;
                    model.personalTaxReturnAdditionalInfo = personalTaxReturnAdditionalInfo;
                    model.companyTaxReturnAdditionalInfo = companyTaxReturnAdditionalInfo;
                    if (isManual) {
                        model.isManual = true;
                        model.noticeDate = noticeDate.toLocaleDateString('en-us');
                        model.sentByUserId = sentByUserId;

                        if (noticeFile) {
                            set_loadingMessage(`Uploading new application files...`);
                            const upLoadResult = await fileApi.uploadTempFiles([noticeFile]);
                            let result = upLoadResult.result;
                            if (result && result.length > 0) {
                                model.noticeTempFileName = result[0].tempName;
                            }
                        }
                    }
                    break;
                case 3: //Other
                    model.reason = reason;
                    break;
            }

            set_loadingMessage('Saving...');
            set_loadingOpen(true);

            const response = await loanApi.setLoanLost(loanId, model);
            if (response.statusCode !== 200) {
                set_loadingOpen(false);
                return;
            }

            refreshEvents.dispatchRefreshEvent();

            handleAdminSuccess('Loan updated successfully and moved to Lost.');

            setTimeout(() => {
                _handleClose();
            }, 600);
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error has occurred. Please try again.');
        } finally {
            set_loadingOpen(false);
        }
    };

    const setIncompleteReasonTypeId = (checked, id) => {
        const index = incompleteReasonTypeIds.indexOf(id);
        if (checked) {
            if (index < 0) {
                set_incompleteReasonTypeIds([...incompleteReasonTypeIds, id]);
            }
        } else {
            if (index > -1) {
                set_incompleteReasonTypeIds(incompleteReasonTypeIds.filter((i) => i !== id));
            }
        }
    };

    const onFileSelectSubmit = (id, files) => {
        if (!files || files.length === 0) {
            return;
        }

        set_noticeFile(files[0]);
        set_fileSelectOptions({ ...fileSelectOptions, isOpen: false });
    };

    const onFileSelectCancel = () => {
        set_fileSelectOptions({ ...fileSelectOptions, isOpen: false });
    };

    const openFileSelect = () => {
        const options = {
            ownerId: null,
            isOpen: true,
            maxFiles: 1,
            fileTypeName: 'Notice File',
        };
        set_fileSelectOptions(options);
    };

    const getNoticeTitle = () => {
        if (lostTypeId === 2 && !isManual) {
            return "The notice 'Incomplete File' will be scheduled to be sent after Save";
        } else {
            return 'Current changes will not trigger a notice.';
        }
    };

    const RenderTaxReturnHelp = () => {
        return (
            <FontAwesomeIcon
                icon={faQuestionCircle}
                style={{
                    marginLeft: '5px',
                    color: '#2196f3',
                    cursor: 'pointer',
                }}
                onClick={openTaxReturnHelp}
            />
        );
    };

    const openTaxReturnHelp = () => {
        set_informModalTitle('Tax Filing');
        set_informModalText(
            "State what is missing in YYYY format along with 'return' and/or 'extension'.<br><br><b>REQUIREMENTS:</b> Most recent year AND if under extension, prior year also. Cutoff dates are April 1 and October 1 based on date application received. E.g. Application received date of April 1 – September 30, 2025 require 2024 return or 2024 extension + 2023 return. If October 1 – March 31, require 2024 return.<br><br><b>REQUIREMENTS, SBA:</b> Typically require 3 years of tax returns.</p>"
        );
        set_informModalOpen(true);
    };

    if (!isOpen || !loan) {
        return null;
    }

    return (
        <>
            <Dialog
                maxWidth='sm'
                open={isOpen}
                onClose={_handleClose}
                aria-labelledby='form-dialog-title'
            >
                <DialogContent>
                    <div className='modal-container'>
                        <div className='modal-backdrop'></div>
                        <div
                            className='modal'
                            tabIndex={-1}
                            role='dialog'
                            style={{ width: '700px' }}
                        >
                            <div
                                className='modal-dialog'
                                role='document'
                            >
                                <form
                                    name='loanLostForm'
                                    onSubmit={(event) => onSubmit(event)}
                                >
                                    <div>
                                        <div
                                            className='page-header'
                                            role='banner'
                                        >
                                            <h1 style={{ whiteSpace: 'nowrap' }}>Lost Loan</h1>
                                            <button
                                                type='button'
                                                className='close'
                                                data-dismiss='modal'
                                                aria-label='Close'
                                                onClick={_handleClose}
                                            >
                                                <span aria-hidden='true'>&times;</span>
                                            </button>
                                        </div>
                                        <div>
                                            <div
                                                style={styles.form}
                                                className={'standard-form'}
                                            >
                                                <div
                                                    className='row'
                                                    style={styles.formRow}
                                                >
                                                    <div
                                                        className='columns small-12 large-12'
                                                        style={{
                                                            padding: '5px',
                                                            textAlign: 'left',
                                                            color: lostTypeId === 2 && !isManual ? 'darkred' : 'gray',
                                                        }}
                                                    >
                                                        &nbsp;
                                                        <i title={getNoticeTitle()}>
                                                            <FontAwesomeIcon icon={faPencil} />
                                                        </i>
                                                        &nbsp;&nbsp;
                                                        {getNoticeTitle()}
                                                    </div>
                                                </div>
                                                <div
                                                    className='row'
                                                    style={styles.formRow}
                                                >
                                                    <div className='columns small-12 large-12'>
                                                        <label htmlFor='lostTypeId'>
                                                            How was the Loan Lost?
                                                            <span className='req'>*</span>
                                                        </label>
                                                        <select
                                                            id={'lostTypeId'}
                                                            value={lostTypeId}
                                                            onChange={(e) => set_lostTypeId(parseInt(e.target.value))}
                                                        >
                                                            {lostOptions.map((fs) => {
                                                                return (
                                                                    <option
                                                                        key={fs.id}
                                                                        value={fs.id}
                                                                    >
                                                                        {fs.name}
                                                                    </option>
                                                                );
                                                            })}
                                                        </select>
                                                    </div>
                                                </div>

                                                <div
                                                    className='row'
                                                    style={styles.formRow}
                                                >
                                                    {lostTypeId === 1 && (
                                                        <div className='columns small-12 large-3'>
                                                            <label htmlFor='reason'>
                                                                Withdrawal Date <span className='req'>*</span>
                                                            </label>
                                                            <DateBox
                                                                id='withdrawalDate'
                                                                value={withdrawalDate ? new Date(withdrawalDate) : null}
                                                                onValueChange={set_withdrawalDate}
                                                                required={true}
                                                                placeholder='MM/DD/YYYY'
                                                                useMaskBehavior={true}
                                                                displayFormat='shortdate'
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                                {lostTypeId === 2 && (
                                                    <>
                                                        <div
                                                            className='row'
                                                            style={styles.formRow}
                                                        >
                                                            <div
                                                                className='columns small-12 large-12'
                                                                style={styles.formColumn}
                                                            >
                                                                <input
                                                                    type='radio'
                                                                    id='isAuto'
                                                                    name='isManual'
                                                                    value='0'
                                                                    checked={isManual === false}
                                                                    onChange={(e) => set_isManual(e.target.value === '1')}
                                                                />
                                                                &nbsp; <b>Automatic Notice</b>
                                                                &nbsp;&nbsp;
                                                                <input
                                                                    type='radio'
                                                                    id='isManual'
                                                                    name='isManual'
                                                                    value='1'
                                                                    checked={isManual === true}
                                                                    onChange={(e) => set_isManual(e.target.value === '1')}
                                                                />
                                                                &nbsp; <b>Manual Notice</b>
                                                            </div>
                                                        </div>
                                                        <div
                                                            className='row'
                                                            style={styles.formRow}
                                                        >
                                                            <div className='columns small-12 large-12'>
                                                                <label htmlFor='reason'>
                                                                    Reasons for Closing File <span className='req'>*</span>
                                                                </label>
                                                                {incompleteReasons &&
                                                                    incompleteReasons.map((r, index) => {
                                                                        if (r.key === IncompleteReasonType.CompanyTaxReturn) {
                                                                            return (
                                                                                <div
                                                                                    key={`key_${index}`}
                                                                                    style={{
                                                                                        display: 'flex',
                                                                                        flexDirection: 'row',
                                                                                        alignItems: 'flex-start',
                                                                                    }}
                                                                                >
                                                                                    <input
                                                                                        style={{
                                                                                            marginTop: '3px',
                                                                                        }}
                                                                                        id={`incompleteTypeId_${r.key}`}
                                                                                        type='checkbox'
                                                                                        value={r.key}
                                                                                        checked={
                                                                                            incompleteReasonTypeIds.indexOf(
                                                                                                r.key
                                                                                            ) > -1
                                                                                        }
                                                                                        onChange={(e) =>
                                                                                            setIncompleteReasonTypeId(
                                                                                                e.target.checked,
                                                                                                r.key
                                                                                            )
                                                                                        }
                                                                                    />
                                                                                    &nbsp;
                                                                                    {r.name}
                                                                                    <input
                                                                                        type='text'
                                                                                        id='companyTaxReturnAdditionalInfo'
                                                                                        value={companyTaxReturnAdditionalInfo}
                                                                                        onChange={(e) =>
                                                                                            set_companyTaxReturnAdditionalInfo(
                                                                                                e.target.value
                                                                                            )
                                                                                        }
                                                                                        required={false}
                                                                                        disabled={false}
                                                                                        maxLength={255}
                                                                                        style={{
                                                                                            marginLeft: '10px',
                                                                                            width: '450px',
                                                                                        }}
                                                                                    />
                                                                                    {RenderTaxReturnHelp()}
                                                                                </div>
                                                                            );
                                                                        } else if (
                                                                            r.key === IncompleteReasonType.PersonalTaxReturn
                                                                        ) {
                                                                            return (
                                                                                <div
                                                                                    key={`key_${index}`}
                                                                                    style={{
                                                                                        display: 'flex',
                                                                                        flexDirection: 'row',
                                                                                        alignItems: 'flex-start',
                                                                                    }}
                                                                                >
                                                                                    <input
                                                                                        style={{
                                                                                            marginTop: '3px',
                                                                                        }}
                                                                                        id={`incompleteTypeId_${r.key}`}
                                                                                        type='checkbox'
                                                                                        value={r.key}
                                                                                        checked={
                                                                                            incompleteReasonTypeIds.indexOf(
                                                                                                r.key
                                                                                            ) > -1
                                                                                        }
                                                                                        onChange={(e) =>
                                                                                            setIncompleteReasonTypeId(
                                                                                                e.target.checked,
                                                                                                r.key
                                                                                            )
                                                                                        }
                                                                                    />
                                                                                    &nbsp;
                                                                                    {r.name}
                                                                                    <input
                                                                                        type='text'
                                                                                        id='personalTaxReturnAdditionalInfo'
                                                                                        value={personalTaxReturnAdditionalInfo}
                                                                                        onChange={(e) =>
                                                                                            set_personalTaxReturnAdditionalInfo(
                                                                                                e.target.value
                                                                                            )
                                                                                        }
                                                                                        required={false}
                                                                                        disabled={false}
                                                                                        maxLength={255}
                                                                                        style={{
                                                                                            marginLeft: '10px',
                                                                                            width: '450px',
                                                                                        }}
                                                                                    />
                                                                                    {RenderTaxReturnHelp()}
                                                                                </div>
                                                                            );
                                                                        } else {
                                                                            return (
                                                                                <div
                                                                                    key={`key_${index}`}
                                                                                    style={{
                                                                                        display: 'flex',
                                                                                        flexDirection: 'row',
                                                                                        alignItems: 'flex-start',
                                                                                    }}
                                                                                >
                                                                                    <input
                                                                                        style={{
                                                                                            marginTop: '3px',
                                                                                        }}
                                                                                        id={`incompleteTypeId_${r.key}`}
                                                                                        type='checkbox'
                                                                                        value={r.key}
                                                                                        checked={
                                                                                            incompleteReasonTypeIds.indexOf(
                                                                                                r.key
                                                                                            ) > -1
                                                                                        }
                                                                                        onChange={(e) =>
                                                                                            setIncompleteReasonTypeId(
                                                                                                e.target.checked,
                                                                                                r.key
                                                                                            )
                                                                                        }
                                                                                    />
                                                                                    &nbsp;
                                                                                    {r.name}
                                                                                </div>
                                                                            );
                                                                        }
                                                                    })}
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                                {lostTypeId === 3 && (
                                                    <div
                                                        className='row'
                                                        style={styles.formRow}
                                                    >
                                                        <div className='columns small-12 large-12'>
                                                            <label htmlFor='reason'>
                                                                Details
                                                                <span className='req'>*</span>
                                                            </label>
                                                            <textarea
                                                                style={{
                                                                    width: '100%',
                                                                }}
                                                                rows={3}
                                                                id='reason'
                                                                value={reason}
                                                                onChange={(e) => set_reason(e.target.value)}
                                                                required={true}
                                                                placeholder='Please provide more details about why this loan was lost...'
                                                                maxLength={400}
                                                            />
                                                        </div>
                                                    </div>
                                                )}

                                                {lostTypeId === 2 && isManual && (
                                                    <>
                                                        <div
                                                            className='row'
                                                            style={styles.formRow}
                                                        >
                                                            {
                                                                <div
                                                                    className='columns small-12 large-3'
                                                                    style={styles.formColumn}
                                                                >
                                                                    <label htmlFor='noticeDate'>
                                                                        Notice Date <span className='req'>*</span>
                                                                    </label>
                                                                    <DateBox
                                                                        id='noticeDate'
                                                                        value={noticeDate ? new Date(noticeDate) : null}
                                                                        onValueChange={set_noticeDate}
                                                                        required={true}
                                                                        placeholder='MM/DD/YYYY'
                                                                        useMaskBehavior={true}
                                                                        displayFormat='shortdate'
                                                                    />
                                                                </div>
                                                            }
                                                            <div
                                                                className='columns small-12 large-3'
                                                                style={{
                                                                    paddingTop: '35px',
                                                                    paddingBottom: '0px',
                                                                }}
                                                            >
                                                                <button
                                                                    type='button'
                                                                    className='button hollow'
                                                                    title='Upload Notice File'
                                                                    onClick={openFileSelect}
                                                                >
                                                                    Add Notice File
                                                                </button>
                                                                &nbsp;
                                                            </div>
                                                            <div
                                                                className='columns small-12 large-6'
                                                                style={{
                                                                    marginTop: '35px',
                                                                    paddingBottom: '0px',
                                                                }}
                                                            >
                                                                {noticeFile && <span>File: {noticeFile.name}</span>}
                                                            </div>
                                                        </div>
                                                        <div
                                                            className='row'
                                                            style={styles.formRow}
                                                        >
                                                            <div
                                                                className='columns small-12 large-6'
                                                                style={styles.formColumn}
                                                            >
                                                                <label htmlFor='responsibleId'>
                                                                    Notice Sent By <span className='req'>*</span>
                                                                </label>
                                                                <select
                                                                    id='sentById'
                                                                    value={sentByUserId}
                                                                    onChange={(event) => set_sentByUserId(event.target.value)}
                                                                    required={true}
                                                                >
                                                                    <option
                                                                        key=''
                                                                        value=''
                                                                    ></option>
                                                                    {employees &&
                                                                        employees.map((poc) => {
                                                                            return (
                                                                                <option
                                                                                    key={poc.key}
                                                                                    value={poc.key}
                                                                                >
                                                                                    {poc.name}
                                                                                </option>
                                                                            );
                                                                        })}
                                                                </select>
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                            <div style={{ padding: '5px 5px' }}>
                                                <div
                                                    className='button-footer'
                                                    style={styles.buttonContainer}
                                                >
                                                    <button
                                                        style={styles.buttonMarginRight}
                                                        type='submit'
                                                        className='button primary'
                                                        disabled={!canSubmit()}
                                                    >
                                                        Submit
                                                    </button>
                                                    <button
                                                        type='button'
                                                        className='button hollow'
                                                        data-dismiss='modal'
                                                        onClick={_handleClose}
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            <LoadPanel
                hideOnOutsideClick={false}
                visible={loadingOpen}
                message={loadingMessage}
            />
            <FileSelectDialog
                options={fileSelectOptions}
                onSubmit={onFileSelectSubmit}
                onCancel={onFileSelectCancel}
            />
            <InformModal
                isOpen={informModalOpen}
                title={informModalTitle}
                text={informModalText}
                confirmCallback={() => set_informModalOpen(false)}
            />
        </>
    );
};

export default LoseLoanModal;
